import React, { Fragment } from "react";
import { Row, Table } from "reactstrap";
import { Link } from "react-router-dom";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  Header,
} from "@tanstack/react-table";
import Select from "react-select";
import { TData } from "gridjs/dist/src/types";
import renderPageNumbers from "pages/utils/renderPageNumbers";
import renderPageNumbersMobile from "pages/utils/renderPageNumbersMobile";

interface TableContainerProps {
  columns?: any;
  data?: any;
  numberOfRows?: any;
  setPageSize?: any;
  setPageIndex?: any;
  pageSize?: any;
  pageIndex?: any;
  setSorting?: any;
  sorting?: any;
  handleTaskClick?: any;
  tableClass?: any;
  theadClass?: any;
  trClass?: any;
  thClass?: any;
  divClass?: any;
  onRowDoubleClick?: (rowData: any) => void;
}

const TableContainer = ({
  columns,
  data,
  numberOfRows,
  setPageSize,
  setPageIndex,
  setSorting,
  sorting,
  pageSize,
  pageIndex,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  onRowDoubleClick,
}: TableContainerProps) => {
  const table = useReactTable({
    columns,
    data,
    manualPagination: true,

    onSortingChange: setSorting,
    enableMultiSort: true,
    manualSorting: true,
    sortDescFirst: true,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),

    state: {
      sorting,
    },
  });

  const { getHeaderGroups, getRowModel } = table;

  const limitOptions = [
    { label: "10", value: 10 },
    { label: "25", value: 25 },
    { label: "50", value: 50 },
    { label: "100", value: 100 },
  ];

  //Pagination
  function getCanPreviousPage() {
    return pageIndex > 0;
  }

  function getCanNextPage() {
    return pageIndex < Math.ceil(numberOfRows / pageSize) - 1;
  }

  function getPageIndexOptions() {
    let options = [];
    for (let i = 0; i < Math.ceil(numberOfRows / pageSize); i++) {
      options.push(i);
    }
    return options;
  }

  function previousPage() {
    return setPageIndex(pageIndex - 1);
  }

  function nextPage() {
    return setPageIndex(pageIndex + 1);
  }

  const sortToggler = (header: Header<TData, unknown>) => {
    if (header.column.getCanSort()) {
      header.column.toggleSorting(
        header.column.getIsSorted() === "desc",
        false
      );
    }
  };

  return (
    <Fragment>
      <div className={divClass}>
        <Table hover className={tableClass}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup: any) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header: any) => (
                  <th
                    key={header.id}
                    className={thClass}
                    {...{
                      onClick: header.column.getToggleSortingHandler(),
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <React.Fragment>
                        <div
                          onClick={() => sortToggler(header)}
                          className="hover:cursor-pointer"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {(header.column.getIsSorted() === "asc" ||
                            header.column.getIsSorted() === "desc") && (
                            <span>
                              {header.column.getIsSorted() === "asc" && (
                                <i className="ri-arrow-up-line"></i>
                              )}
                              {header.column.getIsSorted() === "desc" && (
                                <i className="ri-arrow-down-line"></i>
                              )}
                            </span>
                          )}
                        </div>
                      </React.Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map((row: any) => {
              return (
                <tr key={row.id} onDoubleClick={() => onRowDoubleClick?.(row.original)}>
                  {row.getVisibleCells().map((cell: any) => {
                    return (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
        <div className="col-sm-auto">
          <Select
            onChange={(e: any) => {
              setPageIndex(0);
              setPageSize(e.value);
            }}
            value={limitOptions.filter((option) => option.value === pageSize)}
            options={limitOptions}
          />
        </div>

        <div className="col-sm">
          <div className="text-muted">
            Showing
            <span className="fw-semibold ms-1">
              {numberOfRows < pageSize ? numberOfRows : pageSize}
            </span>{" "}
            of <span className="fw-semibold">{numberOfRows}</span> Results
          </div>
        </div>
        <div className="col-sm-auto">
          <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
            <li
              className={
                !getCanPreviousPage() ? "page-item disabled" : "page-item"
              }
            >
              <Link to="#" className="page-link" onClick={previousPage}>
                Previous
              </Link>
            </li>
            {window.screen.width <= 575
              ? renderPageNumbersMobile(
                  getPageIndexOptions().length,
                  pageIndex,
                  setPageIndex
                )
              : renderPageNumbers(
                  getPageIndexOptions().length,
                  pageIndex,
                  setPageIndex
                )}
            <li
              className={!getCanNextPage() ? "page-item disabled" : "page-item"}
            >
              <Link to="#" className="page-link" onClick={nextPage}>
                Next
              </Link>
            </li>
          </ul>
        </div>
      </Row>
    </Fragment>
  );
};

export default TableContainer;
